/**
 * EVERY FEATURED CARD PHOTO SHIPS AT THE CARD'S FRAME (2026-09-25)
 *
 * The other half of echBynderMatch. A lennar.com card photo with no Bynder original keeps
 * its own shape on the canvas (#297 stopped squashing it into 270×180), so one email could
 * carry cards 100, 151 and 180 px tall. At submit, each such photo is cropped here — centred,
 * cover-fitted, at twice the frame for sharp phones — hosted through `/sfmc-image` (the
 * same Content Builder path the GIF hero uses), and swapped into the HTML and the block doc
 * with the frame's height pinned again. The result is exactly what the canvas previews.
 *
 * NOTHING HERE CAN BREAK A SEND. A photo whose pixels can't be read (the image proxy has to
 * allow cdn.lennar.com — emailbuilder-be `/bynder-img`), whose shape already fits, or whose
 * upload fails is left exactly as it was; only a photo that was cropped AND hosted changes.
 */
import { echNameOf, echMeta } from "./echRegistry";
import { loadFrame, paintCover } from "./heroGif";
import { hostImage } from "./api";
import { isCdnPhoto } from "./echBynderMatch";

export interface CardCropDeps {
  load: (url: string) => Promise<{ width: number; height: number } & CanvasImageSource>;
  host: (dataUrl: string, name: string) => Promise<{ url: string }>;
  /** draw + encode; injectable because jsdom has no canvas */
  encode: (im: { width: number; height: number } & CanvasImageSource, W: number, H: number) => string;
}

function browserEncode(im: { width: number; height: number } & CanvasImageSource, W: number, H: number): string {
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  paintCover(ctx, im, W, H);
  return c.toDataURL("image/jpeg", 0.86);
}

const DEFAULT_DEPS: CardCropDeps = { load: loadFrame, host: hostImage, encode: browserEncode };

const attrEsc = (v: string) => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** the frame a card declares for its photo — Layne's `PlanImage` width/height, else 270×180 */
export function cardFrame(type: string): { w: number; h: number } {
  const f = (echMeta(type)?.full?.fields || {}) as Record<string, { width?: number; height?: number }>;
  return { w: Number((f.PlanImage && f.PlanImage.width) || 270), h: Number((f.PlanImage && f.PlanImage.height) || 180) };
}

/** within 2% of the frame's shape already — cropping would change nothing a reader can see */
export const fitsFrame = (w: number, h: number, W: number, H: number): boolean =>
  !!w && !!h && Math.abs(w / h - W / H) / (W / H) < 0.02;

/** point every <img> showing `from` at `to`, with the frame's height pinned back on */
export function pinCardImg(html: string, from: string, to: string, h: number): string {
  let out = String(html || "");
  [from, attrEsc(from)].filter((s, i, a) => a.indexOf(s) === i).forEach((src) => {
    let at = out.indexOf('src="' + src + '"');
    while (at > -1) {
      const open = out.lastIndexOf("<img", at);
      const close = out.indexOf(">", at);
      if (open < 0 || close < 0) break;
      let tag = out.slice(open, close + 1).split('src="' + src + '"').join('src="' + to + '"');
      tag = /\sheight="\d+"/.test(tag) ? tag.replace(/\sheight="\d+"/, ' height="' + h + '"') : tag.replace(/^<img/, '<img height="' + h + '"');
      tag = /height\s*:\s*auto/i.test(tag) ? tag.replace(/height\s*:\s*auto/i, "height: " + h + "px") : tag;
      out = out.slice(0, open) + tag + out.slice(close + 1);
      at = out.indexOf('src="' + src + '"', open + tag.length);
    }
  });
  return out;
}

export interface CardCropResult<T> { html: string; doc: T; map: Map<string, string>; heights: Map<string, number>; cropped: number; skipped: string[] }

export async function cropCardPhotos<T extends { type: string; data: Record<string, unknown> }[]>(
  html: string, doc: T, name: string, deps: Partial<CardCropDeps> = {},
): Promise<CardCropResult<T>> {
  const d: CardCropDeps = { ...DEFAULT_DEPS, ...deps };
  const map = new Map<string, string>();
  const heights = new Map<string, number>();
  const skipped: string[] = [];
  const jobs: { url: string; W: number; H: number }[] = [];
  for (const b of doc || []) {
    if (!/^ECH_Featured/.test(echNameOf(b.type) || "")) continue;
    const u = String((b.data || {}).PlanImage || "").trim();
    if (!isCdnPhoto(u) || jobs.some((j) => j.url === u)) continue;
    const fr = cardFrame(b.type);
    jobs.push({ url: u, W: fr.w, H: fr.h });
  }
  let n = 0;
  for (const j of jobs) {
    try {
      const im = await d.load(j.url);
      if (fitsFrame(im.width, im.height, j.W, j.H)) continue;
      const data = d.encode(im, j.W * 2, j.H * 2);
      const { url } = await d.host(data, name + "_card" + (++n));
      if (!url) { skipped.push(j.url); continue; }
      map.set(j.url, url);
      heights.set(j.url, j.H);
    } catch (e) {
      skipped.push(j.url);
    }
  }
  let out = html;
  map.forEach((to, from) => { out = pinCardImg(out, from, to, heights.get(from) || 180); });
  const nextDoc = (doc || []).map((b) => {
    const u = String((b.data || {}).PlanImage || "").trim();
    const to = map.get(u);
    return to ? { ...b, data: { ...b.data, PlanImage: to, PlanImage__nat: "" } } : b;
  }) as unknown as T;
  return { html: out, doc: nextDoc, map, heights, cropped: map.size, skipped };
}

/* ── CAN THIS BROWSER CROP A LENNAR.COM PHOTO AT ALL? (probed once per page) ─────────────
   The canvas previews a card photo at its frame only when the submit will really crop it —
   otherwise the preview and the sent email would disagree. Cropping needs the photo's
   pixels, which only the image proxy can hand over (cdn.lennar.com sends no CORS). So the
   first lennar.com card photo on the canvas is fetched through the proxy once; the answer
   holds for the page. */
let cropReady: boolean | null = null;
let cropProbe: Promise<boolean> | null = null;
export function cdnCropReady(): boolean { return cropReady === true; }
/* off under jest unless a suite turns it on — see echBynderMatch for the same reason */
let probeOn = typeof process === "undefined" || !process.env || process.env.NODE_ENV !== "test";
export function __setCdnCropProbe(on: boolean): void { probeOn = on; }
export function probeCdnCrop(sampleUrl: string, load: (u: string) => Promise<unknown> = loadFrame): Promise<boolean> {
  if (!probeOn) return Promise.resolve(false);
  if (cropProbe) return cropProbe;
  cropProbe = load(sampleUrl).then(() => { cropReady = true; return true; }).catch(() => { cropReady = false; return false; });
  return cropProbe;
}
export function __resetCdnCrop(): void { cropReady = null; cropProbe = null; }
